import { useState } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentConfigService, type AgentConfig } from '@/services/agent-config-service';
import {
  Empty,
  Spinner,
  Button,
  Badge,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  toast,
} from 'prosci-ui';

const CATEGORIES = [
  { id: 'search', label: 'Search & Retrieval' },
  { id: 'llm', label: 'LLM & Models' },
  { id: 'features', label: 'Features' },
  { id: 'memory', label: 'Memory' },
];

export function AgentSettingsTab() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<unknown>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['agentConfigs'],
    queryFn: () => agentConfigService.list(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, value }: { id: string; value: unknown }) =>
      agentConfigService.update(id, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentConfigs'] });
      toast.success('Pending change saved');
      setEditingId(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update'),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => agentConfigService.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentConfigs'] });
      toast.success('Published');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to publish'),
  });

  const discardMutation = useMutation({
    mutationFn: (id: string) => agentConfigService.discard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentConfigs'] });
      toast.success('Discarded');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to discard'),
  });

  const publishAllMutation = useMutation({
    mutationFn: () => agentConfigService.publishAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentConfigs'] });
      toast.success('All changes published');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to publish all'),
  });

  const configs: AgentConfig[] =
    extractAttributesList(data);

  const hasPendingChanges = configs.some((c) => c.has_pending_changes);

  // A config with depends_on is annotated with its dependency and stays
  // read-only until the dependency's PUBLISHED value is on (runtime only
  // reads published values).
  const findDependency = (config: AgentConfig): AgentConfig | null =>
    config.depends_on ? configs.find((c) => c.key === config.depends_on) ?? null : null;

  const startEdit = (config: AgentConfig) => {
    setEditingId(config.id);
    setEditValue(config.has_pending_changes ? config.pending_value : config.published_value);
  };

  if (isLoading) {
    return <Empty><Spinner /></Empty>;
  }

  return (
    <Tabs defaultValue="search">
      <div className="flex items-center justify-between mb-4">
        <TabsList>
          {CATEGORIES.map((cat) => (
            <TabsTrigger key={cat.id} value={cat.id}>{cat.label}</TabsTrigger>
          ))}
        </TabsList>
        <div className="flex items-center gap-2">
          {hasPendingChanges && (
            <>
              <Badge variant="outline" className="border-amber-400 text-amber-700">
                Unpublished changes
              </Badge>
              <Button
                size="sm"
                onClick={() => publishAllMutation.mutate()}
                disabled={publishAllMutation.isPending}
              >
                {publishAllMutation.isPending ? 'Publishing...' : 'Publish All'}
              </Button>
            </>
          )}
        </div>
      </div>

      {CATEGORIES.map((cat) => {
        const items = configs.filter((c) => c.category === cat.id);
        return (
          <TabsContent key={cat.id} value={cat.id}>
            <div className="flex items-center justify-between text-xs text-muted-foreground px-1 pb-2 mb-1 border-b">
              <span>Setting</span>
              <span>Value <span className="text-muted-foreground/60">&middot; click to edit</span></span>
            </div>
            <div className="divide-y">
              {items.map((config) => (
                <ConfigRow
                  key={config.id}
                  config={config}
                  dependency={findDependency(config)}
                  isEditing={editingId === config.id}
                  editValue={editingId === config.id ? editValue : null}
                  onStartEdit={() => startEdit(config)}
                  onCancelEdit={() => setEditingId(null)}
                  onSave={(value) => updateMutation.mutate({ id: config.id, value })}
                  onEditValueChange={setEditValue}
                  onPublish={() => publishMutation.mutate(config.id)}
                  onDiscard={() => discardMutation.mutate(config.id)}
                  isSaving={updateMutation.isPending}
                />
              ))}
            </div>
          </TabsContent>
        );
      })}
    </Tabs>
  );
}

function ConfigRow({
  config,
  dependency,
  isEditing,
  editValue,
  onStartEdit,
  onCancelEdit,
  onSave,
  onEditValueChange,
  onPublish,
  onDiscard,
  isSaving,
}: {
  config: AgentConfig;
  dependency: AgentConfig | null;
  isEditing: boolean;
  editValue: unknown;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (value: unknown) => void;
  onEditValueChange: (value: unknown) => void;
  onPublish: () => void;
  onDiscard: () => void;
  isSaving: boolean;
}) {
  const displayValue = config.has_pending_changes ? config.pending_value : config.published_value;
  const dependencyMet =
    !dependency || dependency.published_value === true || dependency.published_value === 'true';

  return (
    <div className={`flex items-center justify-between gap-6 py-3 px-1${config.has_pending_changes ? ' bg-amber-50/40 dark:bg-amber-950/10 -mx-1 px-2 rounded' : ''}`}>
      {/* Left: label + description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium">{config.label || config.key}</span>
          {dependency && (
            <span className="text-xs text-muted-foreground">
              (Requires {dependencyName(dependency)} Enabled)
            </span>
          )}
          {config.has_pending_changes && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-400 text-amber-600">
              pending
            </Badge>
          )}
        </div>
        {config.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{config.description}</p>
        )}
      </div>

      {/* Right: value / editor */}
      <div className="flex items-center gap-2 shrink-0">
        {isEditing ? (
          <>
            <ConfigValueInput config={config} value={editValue} onChange={onEditValueChange} />
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button size="sm" className="text-xs h-7" onClick={() => onSave(editValue)} disabled={isSaving}>
              Save
            </Button>
          </>
        ) : config.has_pending_changes ? (
          <>
            <span className="text-xs text-muted-foreground line-through mr-1">
              {formatValue(config.published_value, config.value_type)}
            </span>
            <span
              className="text-sm font-mono text-amber-700 dark:text-amber-400 cursor-pointer hover:underline"
              onClick={onStartEdit}
            >
              {formatValue(config.pending_value, config.value_type)}
            </span>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onDiscard}>
              Discard
            </Button>
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={onPublish}>
              Publish
            </Button>
          </>
        ) : !dependencyMet ? (
          <span
            className="text-sm font-mono bg-muted px-2 py-0.5 rounded opacity-50 cursor-not-allowed"
            title={`Enable ${dependencyName(dependency!)} to edit this setting`}
          >
            {formatValue(displayValue, config.value_type)}
          </span>
        ) : (
          <span
            className="text-sm font-mono bg-muted px-2 py-0.5 rounded cursor-pointer hover:bg-muted/80 transition-colors"
            onClick={onStartEdit}
          >
            {formatValue(displayValue, config.value_type)}
          </span>
        )}
      </div>
    </div>
  );
}

function ConfigValueInput({
  config,
  value,
  onChange,
}: {
  config: AgentConfig;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  switch (config.value_type) {
    case 'boolean':
      return (
        <Switch
          checked={value === true || value === 'true'}
          onCheckedChange={(checked: boolean) => onChange(checked)}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          className="w-24 h-7 text-sm font-mono"
          value={value as number ?? ''}
          min={config.validation_rules?.min}
          max={config.validation_rules?.max}
          step={Number.isInteger(config.default_value as number) ? 1 : 0.1}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value;
            onChange(v.includes('.') ? parseFloat(v) : parseInt(v, 10));
          }}
          autoFocus
        />
      );

    case 'select':
      return (
        <Select value={String(value ?? '')} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="w-40 h-7 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {config.validation_rules?.options?.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'string':
    default:
      return (
        <Input
          type="text"
          className="w-56 h-7 text-sm font-mono"
          value={String(value ?? '')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          autoFocus
        />
      );
  }
}

// "Use Cohere Reranker" reads better as "(Requires Cohere Reranker Enabled)"
function dependencyName(dependency: AgentConfig): string {
  return (dependency.label || dependency.key).replace(/^Use\s+/i, '');
}

function formatValue(value: unknown, type: string): string {
  if (value === null || value === undefined) return '-';

  switch (type) {
    case 'boolean':
      return value === true || value === 'true' ? 'Enabled' : 'Disabled';
    default:
      return String(value);
  }
}
